import { CheckOutlined } from '@ant-design/icons';
import type { UseQueryResult } from '@tanstack/react-query';
import { Alert, Badge, Button, Empty, theme } from 'antd';
import type { ReactNode } from 'react';
import { useShellText } from '@/hooks/useShellText';
import { AccountFeedback } from '../shared/AccountFeedback';
import { AccountTime } from '../shared/AccountTime';
import { useReadNotification } from '../shared/queries';
import type { NotificationPage } from '../shared/types';
import styles from './notifications.module.css';

export function NotificationList({
  query,
  preview = false,
  filter,
}: {
  query: UseQueryResult<NotificationPage>;
  preview?: boolean;
  filter?: ReactNode;
}) {
  const t = useShellText();
  const { token } = theme.useToken();
  const read = useReadNotification();
  return (
    <section
      className={preview ? styles.previewList : undefined}
      aria-label={t('通知列表', 'Notification list')}
    >
      <div className={styles.toolbar}>
        {filter ?? (
          <span>
            {t('未读 {count} 条', '{count} unread', {
              count: query.data?.unreadCount ?? 0,
            })}
          </span>
        )}
        {query.data && !query.isError && (
          <Button
            className={preview ? styles.previewAction : undefined}
            size={preview ? 'small' : 'medium'}
            type={preview ? 'text' : 'default'}
            icon={preview ? undefined : <CheckOutlined aria-hidden />}
            disabled={
              !query.data?.unreadCount ||
              query.isError ||
              (read.isPending && read.variables !== null)
            }
            loading={read.isPending && read.variables === null}
            onClick={() => read.mutate(null)}
          >
            {t('全部标为已读', 'Mark all read')}
          </Button>
        )}
      </div>
      <div className={preview ? styles.previewScroll : undefined}>
        <AccountFeedback
          pending={query.isPending}
          error={query.error}
          retry={() => void query.refetch()}
        />
        {read.error && (
          <Alert
            type="error"
            title={t('操作失败，请重试', 'Action failed; please retry')}
            description={read.error.message}
          />
        )}
        {query.data &&
          !query.isError &&
          (!query.data.items.length ? (
            <Empty
              description={t('暂无通知', 'No notifications')}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
              }}
            >
              {query.data.items.map((item) => (
                <li
                  key={item.id}
                  className={preview ? `${styles.item} ${styles.preview}` : styles.item}
                  style={{
                    padding: preview ? '12px 4px' : '20px 0',
                    borderTop: `1px solid ${token.colorBorderSecondary}`,
                    overflowWrap: 'anywhere',
                  }}
                >
                  <div className={styles.heading}>
                    <Badge
                      color={
                        item.readAt ? token.colorTextQuaternary : token.colorPrimary
                      }
                    />
                    <span style={{ fontWeight: item.readAt ? 400 : 600 }}>
                      {t(item.title, item.titleEn)}
                    </span>
                  </div>
                  <p
                    className={styles.content}
                    style={{
                      color: token.colorTextSecondary,
                      marginBlock: 0,
                    }}
                  >
                    {item.demo
                      ? t(
                          '{recipient}，这是您的第 {number} 条通知，可标记已读并在通知中心回查。',
                          'Notification {number} for {recipient}. Read status is shared with the notification center.',
                          item.demo,
                        )
                      : t(item.content, item.contentEn)}
                  </p>
                  <div
                    className={styles.time}
                    style={{
                      color: token.colorTextSecondary,
                    }}
                  >
                    <AccountTime value={item.createdAt} />
                  </div>
                  <div
                    className={styles.action}
                    style={preview ? { textAlign: 'end' } : undefined}
                  >
                    <Button
                      className={preview ? styles.previewAction : undefined}
                      size="small"
                      type="text"
                      icon={<CheckOutlined aria-hidden />}
                      disabled={!!item.readAt || read.isPending}
                      loading={read.isPending && read.variables === item.id}
                      onClick={() => read.mutate(item.id)}
                    >
                      {item.readAt ? t('已读', 'Read') : t('标为已读', 'Mark read')}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          ))}
      </div>
    </section>
  );
}
